import { useMutation, useQueryClient , useQuery } from '@tanstack/react-query';
import { CampaignService } from '../services/campaigns/campaigns-rest';
import { AxiosError } from 'axios';
import type { ICampaign } from '@clickster/constant-definitions/dist-esm/entities/campaign/types';
import type { IDemographicReport } from '@clickster/business-logic/dist-esm/traffic-sources/external/google/entities/reports/types/demographicReport';
import { IQueryProps, IQueryPropsGQL } from '../interfaces/general/api';
import { useQuery as useApolloQuery } from '@apollo/client';
import { CampaignsGqlService } from '../services/campaigns/campaigns-graphql';
import { API_V2 } from '../lib/axios';
import { useEffect } from 'react';
import { print } from 'graphql';

export const useConversionsUpdate = () => {
  const { mutateAsync, isLoading, isError, isSuccess, error } = useMutation(
    ({ data, forcePixelFiring }: { data: string | undefined; forcePixelFiring: boolean }) =>
      CampaignService.conversionsUpdate({ data, forcePixelFiring }),
    {
      onError: (error: AxiosError) => {
        console.error('Error during conversions update:', error);
      },
    },
  );

  return {
    processConversions: mutateAsync,
    isLoading,
    isError,
    isSuccess,
    error,
  };
};

export const useGetCampaigns = ({ params = {}, handleSuccess, enabled, customKey }: IQueryProps<ICampaign[]>) => {
  const { data, isLoading, isFetching, refetch } = useQuery(
    customKey ? [customKey, params.search] : ['campaigns', params.search],
    () => CampaignService.list(params),
    {
      onSuccess: handleSuccess ? handleSuccess : undefined,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 15, // 5 minutes
      cacheTime: 1000 * 60 * 15, // 5 minutes
      enabled,
    },
  );

  return {
    campaigns: data,
    isLoading,
    isFetching,
    refetch,
  };
};

interface IUseGetCampaignById extends IQueryProps<ICampaign> {
  id: string;
}

export const useGetCampaignById = ({
  id,
  enabled = true,
  params,
  customKey,
  staleTime,
  handleSuccess,
}: IUseGetCampaignById) => {
  const { data: campaign, isLoading, refetch } = useQuery(
    customKey ? [customKey, id] : ['campaign', id],
    () => CampaignService.getById(id, params),
    {
      enabled,
      onSuccess: handleSuccess ? handleSuccess : undefined,
      staleTime,
      refetchOnWindowFocus: false,
    },
  );

  return { campaign, isLoading, refetch };
};

interface IUseUpdateCampaign extends IQueryProps<ICampaign> {
  includeTarget?: boolean;
}

export const useUpdateCampaign = ({ handleSuccess, includeTarget = true }: IUseUpdateCampaign) => {
  const { mutateAsync, isLoading, isError, isSuccess, error } = useMutation(
    ({ data, params }: { data: ICampaign; params?: Record<string, any> }) =>
      CampaignService.update(data, params, includeTarget),
    {
      onSuccess: handleSuccess ? handleSuccess : undefined,
    },
  );

  return {
    updateCampaign: mutateAsync,
    isLoading,
    isError,
    isSuccess,
    error,
  };
};

export const useUpdateByAPIV2Campaign = ({ handleSuccess }: IUseUpdateCampaign) => {
  const { mutateAsync, isLoading, isError, isSuccess, error } = useMutation(
    ({ data, params }: { data: ICampaign; params?: Record<string, any> }) =>
      CampaignService.updateByAPIV2(data, params),
    {
      onSuccess: handleSuccess ? handleSuccess : undefined,
    },
  );

  return {
    updateCampaign: mutateAsync,
    isLoading,
    isError,
    isSuccess,
    error,
  };
};

export const useUpdateByAPIV2CampaignParams = ({ handleSuccess }: IUseUpdateCampaign) => {
  const { mutateAsync, isLoading, isError, isSuccess, error } = useMutation(
    ({ data, params, includeTarget }: { data: ICampaign; params?: Record<string, any>; includeTarget?: boolean }) =>
      CampaignService.updateByAPIV2(data, params, includeTarget),
    {
      onSuccess: handleSuccess ? handleSuccess : undefined,
    },
  );

  return {
    updateCampaign: mutateAsync,
    isLoading,
    isError,
    isSuccess,
    error,
  };
};

export const useGetCampaignsGQL = ({
  filter,
  limit,
  handleSuccess,
  handleError,
  enabled,
}: IQueryPropsGQL<ICampaign[]>) => {
  const { data, refetch, loading } = useApolloQuery(CampaignsGqlService.queries.GET_CAMPAIGNS_QUERY, {
    variables: { filter, limit },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const campaigns = data?.CampaignFindMany;
      if (handleSuccess) handleSuccess(campaigns);
    },
    onError(error) {
      if (handleError) handleError(error);
    },
  });

  return {
    data: data?.CampaignFindMany ?? ([] as ICampaign[]),
    isLoading: loading,
    refetch,
  };
};

export const useCreateCampaign = () => {
  const { mutateAsync, isLoading, isError, isSuccess, error } = useMutation((data: ICampaign) =>
    CampaignService.create(data as ICampaign),
  );

  return {
    createCampaign: mutateAsync,
    isLoading,
    isError,
    isSuccess,
    error,
  };
};

export const useGetCampaignsTanStackGQL = ({
  filter,
  limit,
  handleSuccess,
  handleError,
  enabled = true,
  staleTime,
  customKey
}: IQueryPropsGQL<ICampaign[]>) => {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch, isFetching, error } = useQuery({
    queryKey: ['campaigns-query-gql', filter, limit, customKey],
    queryFn: async ({ signal }) => {
      try {
        const queryString = print(CampaignsGqlService.queries.GET_CAMPAIGNS_QUERY);
        console.log('[useGetCampaignsTanStackGQL] Sending GraphQL query:', {
          queryLength: queryString.length,
          variables: { filter, limit },
        });

        const { data: result } = await API_V2.post('graphql', {
          query: queryString,
          variables: { filter, limit },
        }, { signal });

        console.log('[useGetCampaignsTanStackGQL] GraphQL response:', {
          hasErrors: !!result.errors,
          errors: result.errors,
          dataLength: result.data?.CampaignFindMany?.length || 0,
        });

        if (result.errors) {
          console.error('[useGetCampaignsTanStackGQL] GraphQL errors:', result.errors);
          if (handleError) handleError(result.errors[0]);
          throw result.errors[0];
        }
        const campaigns = result.data?.CampaignFindMany;
        /*  if (handleSuccess) handleSuccess(campaigns); */

        return campaigns;
      } catch (error: any) {
        console.error('[useGetCampaignsTanStackGQL] Error:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        /*  if (handleError) handleError(error as Error); */
        throw error;
      }
    },
    enabled,
    staleTime: staleTime, // 5 minutes
    retry: false, // Don't retry on error
    /* cacheTime: 1000 * 60 * 5, // 5 minutes */
    onSuccess: handleSuccess ? handleSuccess : undefined,
    onError: handleError ? handleError : undefined,
  });


  useEffect(() => {
    const cachedData = queryClient.getQueryData(['campaigns-query-gql', filter, limit]) as ICampaign[];
    if (cachedData && staleTime && handleSuccess) {
      handleSuccess(cachedData as ICampaign[]);
    }
  }, []);


  return {
    data: data ?? ([] as ICampaign[]),
    isLoading,
    isFetching,
    refetch,
    error,
  };
};

export const useGetCampaignsWithAutomationsTanStackGQL = ({
  filter,
  handleSuccess,
  handleError,
  enabled = true,
  staleTime,
  customKey
}: Omit<IQueryPropsGQL<ICampaign[]>, 'limit'>) => {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch, isFetching, error } = useQuery({
    queryKey: ['campaigns-automations-query-gql', filter, customKey],
    queryFn: async ({ signal }) => {
      try {
        const queryString = print(CampaignsGqlService.queries.GET_CAMPAIGNS_WITH_AUTOMATIONS_QUERY);
        console.log('[useGetCampaignsWithAutomationsTanStackGQL] Sending GraphQL query:', {
          queryLength: queryString.length,
          variables: { filter },
        });

        const { data: result } = await API_V2.post('graphql', {
          query: queryString,
          variables: { filter },
        }, { signal });

        console.log('[useGetCampaignsWithAutomationsTanStackGQL] GraphQL response:', {
          hasErrors: !!result.errors,
          errors: result.errors,
          dataLength: result.data?.CampaignFindMany?.length || 0,
          campaignsWithAutomations: result.data?.CampaignFindMany?.filter((c: ICampaign) =>
            (c as any).automations_automation?.length > 0
          ).length || 0,
        });

        if (result.errors) {
          console.error('[useGetCampaignsWithAutomationsTanStackGQL] GraphQL errors:', result.errors);
          if (handleError) handleError(result.errors[0]);
          throw result.errors[0];
        }
        const campaigns = result.data?.CampaignFindMany;

        return campaigns;
      } catch (error: any) {
        console.error('[useGetCampaignsWithAutomationsTanStackGQL] Error:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        throw error;
      }
    },
    enabled,
    staleTime: staleTime,
    retry: false,
    onSuccess: handleSuccess ? handleSuccess : undefined,
    onError: handleError ? handleError : undefined,
  });

  useEffect(() => {
    const cachedData = queryClient.getQueryData(['campaigns-automations-query-gql', filter]) as ICampaign[];
    if (cachedData && staleTime && handleSuccess) {
      handleSuccess(cachedData as ICampaign[]);
    }
  }, []);

  return {
    data: data ?? ([] as ICampaign[]),
    isLoading,
    isFetching,
    refetch,
    error,
  };
};

export const useGetCampaignsWithAutomationsREST = ({
  handleSuccess,
  handleError,
  enabled = true,
  staleTime,
}: {
  handleSuccess?: (data: ICampaign[]) => void;
  handleError?: (error: Error) => void;
  enabled?: boolean;
  staleTime?: number;
}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch, isFetching, error } = useQuery({
    queryKey: ['campaigns-automations-rest'],
    queryFn: async ({ signal }) => {
      try {
        console.log('[useGetCampaignsWithAutomationsREST] Fetching active campaigns with automations');

        const campaigns = await CampaignService.list({
          select: '_id name nickname status automations automations.automation._id automations.automation.type automations.automation.cron trafficSource trafficSource._id trafficSource.name trafficSource.logo trafficSource.api',
          populate: ['automations.automation', 'trafficSource'],
          populateModel: ['AutomatedTask', 'TrafficSource'],
          pageSize: 500, // Maximum allowed page size
          // Filter for active campaigns with automations array that has elements
          searchAttribute: 'status,automations',
          search: 'active,true',
          searchType: 'strict,$length.gt',
        }, signal);

        console.log('[useGetCampaignsWithAutomationsREST] Response:', {
          totalCampaigns: campaigns.length,
          campaignsWithAutomations: campaigns.filter((c: ICampaign) =>
            c.automations && c.automations.length > 0
          ).length,
        });

        return campaigns;
      } catch (error: any) {
        console.error('[useGetCampaignsWithAutomationsREST] Error:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        if (handleError) handleError(error);
        throw error;
      }
    },
    enabled,
    staleTime: staleTime || 1000 * 60 * 5, // 5 minutes default
    retry: false,
    onSuccess: handleSuccess ? handleSuccess : undefined,
    onError: handleError ? handleError : undefined,
  });

  useEffect(() => {
    const cachedData = queryClient.getQueryData(['campaigns-automations-rest']) as ICampaign[];
    if (cachedData && staleTime && handleSuccess) {
      handleSuccess(cachedData as ICampaign[]);
    }
  }, []);

  return {
    data: data ?? ([] as ICampaign[]),
    isLoading,
    isFetching,
    refetch,
    error,
  };
};

export const useCloneCampaign = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading, isError, isSuccess, error } = useMutation(
    ({ data }: { data: { campaignId: string; name?: string } }) =>
      CampaignService.clone({ _id: data.campaignId } as ICampaign),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['campaigns']);
      },
    }
  );

  return {
    cloneCampaign: mutateAsync,
    isLoading,
    isError,
    isSuccess,
    error,
  };
};



interface IDemographicReportQueryProps extends IQueryProps<IDemographicReport[]> {
  campaignId: string;
  target?: 'ageRange' | 'gender' | 'incomeRange' | 'parentalStatus';
  dateRange: { from: string; to: string; moment_name: string };
  type?: string;
}

export const useGetReportByCampaignId = ({
  campaignId,
  target,
  dateRange,
  type,
  handleSuccess,
  handleError,
}: IDemographicReportQueryProps) => {
  const { data, isLoading, refetch } = useQuery(
    ['campaign-report', campaignId, target, type],
    () => CampaignService.getReportByCampaignId({ campaignId, target, dateRange, type }),
    {
      onSuccess: handleSuccess ? handleSuccess : undefined,
      onError: handleError ? handleError : undefined,
      staleTime: 1000 * 60 * 15, // 5 minutes

    }
  );

  return {
    data,
    isLoading,
    refetch,
  };
};
